import { TIMERECORDING_TYPE_OF_WORK_CODES } from '../../../models/complex/time-recording-entry.model';
import { TimesheetEntryState, TimesheetState } from '../timesheet-collection.state';
import { NotificationIdState } from '../../utilities/notification-id.type';

/**
 * Projects a timesheet into the rows the UI renders.
 *
 * This replaces three pieces of legacy behaviour that all lived on mutable models:
 * `TimesheetService.closeGaps` (inserting gap entries), `updateStartAndEndLimits` (which wrote
 * min/max values into `*ModelOptions` in place) and the positional editability flags
 * `TimesheetModel.getTimesheetEntries` stamped onto the entries it returned. All three are
 * derived view state, so they are computed here and never stored.
 *
 * Unlike the legacy projection, entries of a not-yet-submitted confirmation are **included**
 * and flagged `inProgress` instead of being dropped, so the time they occupy is visible rather
 * than appearing as a gap the user is asked to fill.
 */

/** Work codes that mark an entry as travel back from the customer. */
const TRAVEL_BACK_WORK_CODES: string[] = [
    TIMERECORDING_TYPE_OF_WORK_CODES.TRBK,
    TIMERECORDING_TYPE_OF_WORK_CODES.TRAVEL_BACK_STANDBY,
];

/** Work codes that mark an entry as travel to the customer. */
const TRAVEL_TO_WORK_CODES: string[] = [
    TIMERECORDING_TYPE_OF_WORK_CODES.TRTO,
    TIMERECORDING_TYPE_OF_WORK_CODES.TRAVEL_TO_STANDBY,
];

export type TimesheetRow = TimesheetSowRow | TimesheetWorkRow | TimesheetManualRow | TimesheetGapRow | TimesheetEowRow;

type TimesheetRowBase = {
    /** Stable key for `@for` tracking. */
    rowId: string;
    /** ISO instants this row spans; the SOW and EOW markers have `start === end`. */
    start: string;
    end: string;
    /**
     * Inclusive editing bounds derived from the neighbouring rows, so a row can never be moved
     * into its neighbour. Empty when unbounded — the SOW row has no earlier row to bound it,
     * and the EOW row no later one.
     */
    minStart: string;
    maxEnd: string;
};

/** The start-of-work marker; always the first row. */
export type TimesheetSowRow = TimesheetRowBase & { kind: 'sow' };

/** The end-of-work marker; always the last row. */
export type TimesheetEowRow = TimesheetRowBase & { kind: 'eow' };

/**
 * What a follow-up travel-back entry inherits from the travel-back it continues.
 *
 * Structural, so it is projected here; whether the follow-up is *offered* depends on config and
 * is decided by the page.
 */
export type TravelBackOrigin = {
    entryId: string;
    /** the preceding entry's work code, which decides the follow-up's travel type */
    workCode: string;
    notificationId: NotificationIdState;
    isTravelBillable: boolean;
};

/** Unaccounted time between two rows, which the user closes by adding a manual entry. */
export type TimesheetGapRow = TimesheetRowBase & {
    kind: 'gap';
    /** whether this gap is the last thing before the end-of-work marker */
    isBeforeEndOfWork: boolean;
    /**
     * The travel-back entry immediately before this gap, when there is one.
     *
     * Present so the page can offer to continue that journey — a second leg of travel back — and
     * have the new entry inherit the notification and billability of the leg it follows.
     */
    precedingTravelBack?: TravelBackOrigin;
};

/** An entry recorded against a notification by a confirmation's time recording. */
export type TimesheetWorkRow = TimesheetRowBase & {
    kind: 'work';
    entryId: string;
    workCode: string;
    activityType: string;
    meansOfTransport: string;
    distance: number;
    address: string | null;
    isTravelBillable: boolean;
    notificationId: NotificationIdState;
    /** empty when the timesheet holds no metadata for the referenced notification */
    customerName: string;
    /** the owning confirmation is still being worked on: rendered, marked, and not editable */
    inProgress: boolean;
    /**
     * A travel-back entry that is the timesheet's last settled work entry, and so the leg whose
     * kind and means of transport the timesheet may still set.
     */
    isTravelBackEditable: boolean;
    /**
     * A travel-to entry that is the timesheet's first settled work entry, and so the leg whose
     * start the timesheet may still set.
     */
    isTravelToEditable: boolean;
};

/** An entry the user added by hand to close a gap. */
export type TimesheetManualRow = TimesheetRowBase & {
    kind: 'manual';
    entryId: string;
    taskType: string;
    travelType?: TIMERECORDING_TYPE_OF_WORK_CODES;
    address: string | null;
    isTravelBillable: boolean;
    notificationId: NotificationIdState;
};

/**
 * Parses an ISO instant, treating an unparsable one as absent.
 *
 * @param isoTimestamp the timestamp to parse
 * @returns the epoch milliseconds, or undefined when the value does not parse
 */
const epoch = (isoTimestamp: string): number | undefined => {
    const parsed = Date.parse(isoTimestamp);
    return Number.isNaN(parsed) ? undefined : parsed;
};

/**
 * The instant truncated to its minute.
 *
 * Every instant the technician can see or type works to the minute — the instant field drops the
 * seconds of whatever it is handed. So a value derived from the clock has to be truncated before it
 * can stand in for one of them, or it reads as a sub-minute span nobody can account for or close.
 *
 * @param isoTimestamp the instant to truncate
 * @returns the instant at the start of its minute, or the input when it cannot be parsed
 */
const startOfMinute = (isoTimestamp: string): string => {
    const parsed = epoch(isoTimestamp);
    return parsed === undefined ? isoTimestamp : new Date(parsed - (parsed % 60_000)).toISOString();
};

/**
 * The end to read for a gap or end-of-work comparison: the entry's own end when it parses, else
 * its start — an unset end is not "no end", it is one that has not been typed yet, and treating
 * it as absent would silently drop the entry out of every comparison that reads it instead of
 * reporting the zero-length span it actually is. Mirrors `openEnd` in
 * `derive-status-work-entries.ts`, which resolves the same kind of not-yet-known end for a
 * different reason (a status still being worked, rather than a form field not yet filled in).
 *
 * The SOW/EOW markers already set `start === end`, so this is a no-op on them.
 *
 * @param entry the entry or row whose effective end is needed
 * @returns the entry's end as an ISO timestamp, or its start when the end does not parse
 */
const effectiveEnd = (entry: { start: string; end: string }): string =>
    epoch(entry.end) === undefined ? entry.start : entry.end;

/**
 * Whether `later` lies strictly after `earlier`. Unparsable instants never compare as a gap, so
 * an incomplete timesheet does not sprout spurious rows.
 *
 * @param earlier the earlier instant
 * @param later the later instant
 * @returns whether there is a positive span between the two
 */
const isAfter = (earlier: string, later: string): boolean => {
    const from = epoch(earlier);
    const to = epoch(later);
    return from !== undefined && to !== undefined && to > from;
};

/**
 * The end of the day after `now` — the ceiling the legacy `updateStartAndEndLimits` clamped every
 * bound to, so a neighbouring entry with an absurd date cannot open an absurd input range.
 *
 * @param now the current instant as an ISO timestamp
 * @returns the ceiling as an ISO timestamp, or '' when `now` does not parse
 */
const boundCeiling = (now: string): string => {
    const parsed = epoch(now);
    if (parsed === undefined) {
        return '';
    }
    const ceiling = new Date(parsed);
    ceiling.setDate(ceiling.getDate() + 1);
    ceiling.setHours(23, 59, 59, 999);
    return ceiling.toISOString();
};

/**
 * Picks whichever of the two instants is earlier, ignoring absent ones.
 *
 * @param left first instant
 * @param right second instant
 * @returns the earlier of the two, or whichever one parses
 */
const earliest = (left: string, right: string): string => {
    const a = epoch(left);
    const b = epoch(right);
    if (a === undefined) {
        return right;
    }
    if (b === undefined) {
        return left;
    }
    return a <= b ? left : right;
};

/**
 * Picks whichever of the two instants is later, ignoring absent ones.
 *
 * @param left first instant
 * @param right second instant
 * @returns the later of the two, or whichever one parses
 */
const latest = (left: string, right: string): string => {
    const a = epoch(left);
    const b = epoch(right);
    if (a === undefined) {
        return right;
    }
    if (b === undefined) {
        return left;
    }
    return a >= b ? left : right;
};

/**
 * The start of the day before `now` — the floor the legacy bounds are clamped to, mirroring
 * {@link boundCeiling}: a sane lower limit, so a neighbouring entry with an absurd date cannot
 * open an absurd input range.
 *
 * @param now the current instant as an ISO timestamp
 * @returns the floor as an ISO timestamp, or '' when `now` does not parse
 */
const boundFloor = (now: string): string => {
    const parsed = epoch(now);
    if (parsed === undefined) {
        return '';
    }
    const floor = new Date(parsed);
    floor.setDate(floor.getDate() - 1);
    floor.setHours(0, 0, 0, 0);
    return floor.toISOString();
};

/**
 * How far a row's end may be moved: up to the row that follows it, and never past the ceiling.
 *
 * The travel-back leg the timesheet may still speak for is the exception, and only where the end
 * of work is what follows it: extending that leg moves the end of work out with it, so bounding
 * the leg there would forbid exactly the edit that is now allowed. An entry after the leg still
 * bounds it — that entry is not the timesheet's to move.
 *
 * @param row the row being bounded
 * @param next the row that follows it, absent for the last one
 * @param ceiling the clock ceiling every bound is clamped to
 * @returns the bound as an ISO instant, '' when unbounded
 */
const maxEndOf = (row: TimesheetRow, next: TimesheetRow | undefined, ceiling: string): string => {
    if (!next) {
        return '';
    }
    if (next.kind === 'eow' && row.kind === 'work' && row.isTravelBackEditable) {
        return ceiling;
    }
    return earliest(next.start, ceiling);
};

/**
 * How far a row's start may be moved: back to the row that precedes it, and never past the floor.
 *
 * The travel-to leg the timesheet may still speak for is the exception, and only where the start
 * of work is what precedes it: pulling that leg's start earlier moves the start of work back with
 * it, so bounding the leg there would forbid exactly the edit that is now allowed. An entry before
 * the leg still bounds it — that entry is not the timesheet's to move.
 *
 * @param row the row being bounded
 * @param previous the row that precedes it, absent for the first one
 * @param floor the clock floor every bound is clamped to
 * @returns the bound as an ISO instant, '' when unbounded
 */
const minStartOf = (row: TimesheetRow, previous: TimesheetRow | undefined, floor: string): string => {
    if (!previous) {
        return '';
    }
    if (previous.kind === 'sow' && row.kind === 'work' && row.isTravelToEditable) {
        return floor;
    }
    return latest(effectiveEnd(previous), floor);
};

/**
 * Projects one entry into its row, without the neighbour-derived bounds or positional flags —
 * those need the whole list and are applied afterwards.
 *
 * @param entry the entry to project
 * @returns the row for this entry
 */
const toEntryRow = (entry: TimesheetEntryState): TimesheetWorkRow | TimesheetManualRow => {
    const shared = {
        rowId: entry.entryId,
        entryId: entry.entryId,
        start: entry.start,
        end: entry.end,
        minStart: '',
        maxEnd: '',
        address: entry.address,
        isTravelBillable: entry.isTravelBillable,
        notificationId: entry.notificationId,
    };

    if (entry.kind === 'manual') {
        return {
            ...shared,
            kind: 'manual',
            taskType: entry.taskType,
            ...(entry.travelType === undefined ? {} : { travelType: entry.travelType }),
        };
    }

    return {
        ...shared,
        kind: 'work',
        workCode: entry.workCode,
        activityType: entry.activityType,
        meansOfTransport: entry.meansOfTransport,
        distance: entry.distance,
        customerName: entry.customerName,
        inProgress: !entry.isConfirmationSubmitted,
        isTravelBackEditable: false,
        isTravelToEditable: false,
    };
};

/**
 * The end-of-work instant, matching `TimesheetModel.getEndOfWork` for an open timesheet: the
 * later of the last entry's end and the recorded end of work, falling back to `now` when the
 * timesheet has neither.
 *
 * Only ever clamped toward an actual last entry — never toward `startOfWork` — because a
 * timesheet with no entries yet has nothing to protect from overflowing: clamping to
 * `startOfWork` in that case would silently repair a start of work recorded after the end of
 * work instead of leaving the two markers to disagree where the form can catch it.
 *
 * @param timesheet the timesheet being projected
 * @param now the current instant as an ISO timestamp
 * @returns the end-of-work instant as an ISO timestamp
 */
const resolveEndOfWork = (timesheet: TimesheetState, now: string): string => {
    const lastEntry = timesheet.entries.at(-1);
    const lastEntryEnd = lastEntry ? effectiveEnd(lastEntry) : undefined;
    if (lastEntryEnd !== undefined && isAfter(timesheet.endOfWork, lastEntryEnd)) {
        return lastEntryEnd;
    }
    // The clock fallback is truncated: this instant is what the form seeds its end-of-work field
    // with, so its seconds would be written back on the next edit and show up as a gap of under a
    // minute between it and a start of work the technician had just typed to the same minute.
    // `TimesheetModel.getEndOfWork`'s counterpart truncates for the same reason.
    return timesheet.endOfWork || lastEntryEnd || timesheet.startOfWork || startOfMinute(now);
};

/**
 * Projects a timesheet into its rows: the SOW marker, the entries in chronological order with
 * gap rows filling the unaccounted spans between them, and the EOW marker.
 *
 * `now` is a parameter rather than read from the clock so the projection stays pure and
 * testable; it is only consulted for the two things the legacy models also derived from the
 * current time — the end-of-work fallback and the bound ceiling.
 *
 * @param timesheet the timesheet to project
 * @param now the current instant as an ISO timestamp
 * @returns the rows to render, in display order
 */
export function buildTimesheetRows(timesheet: TimesheetState, now: string): TimesheetRow[] {
    const entryRows = timesheet.entries.map(toEntryRow);

    // The two positional editability rules left of the legacy `getTimesheetEntries`: a travel-back
    // entry that is the last work entry of the timesheet is the leg the timesheet may still speak
    // for, and symmetrically a travel-to entry that is the first work entry is the leg whose start
    // it may still speak for.
    //
    // An in-progress row is passed over, which is what keeps the flag on a row that can act on it:
    // an open confirmation owns its entries outright, so such a row may not be marked editable and
    // an editable neighbour must not lose the mark to one. Legacy landed on the same rows for a
    // different reason — it left the entries of an open confirmation out of the timesheet
    // altogether, so they were never candidates.
    const settled = (row: TimesheetRow): row is TimesheetWorkRow => row.kind === 'work' && !row.inProgress;

    const firstWorkRow = entryRows.find(settled);
    if (firstWorkRow && TRAVEL_TO_WORK_CODES.includes(firstWorkRow.workCode)) {
        firstWorkRow.isTravelToEditable = true;
    }

    const lastWorkRow = entryRows.findLast(settled);
    if (lastWorkRow && TRAVEL_BACK_WORK_CODES.includes(lastWorkRow.workCode)) {
        lastWorkRow.isTravelBackEditable = true;
    }

    const endOfWork = resolveEndOfWork(timesheet, now);
    const sowRow: TimesheetSowRow = {
        kind: 'sow',
        rowId: 'sow',
        start: timesheet.startOfWork,
        end: timesheet.startOfWork,
        minStart: '',
        maxEnd: '',
    };
    const eowRow: TimesheetEowRow = {
        kind: 'eow',
        rowId: 'eow',
        start: endOfWork,
        end: endOfWork,
        minStart: '',
        maxEnd: '',
    };

    // Bounds come from the immediate neighbours in this list, before gap rows are woven in — a
    // gap is exactly the span between two neighbours, so it never bounds anything itself.
    const bounded = [sowRow, ...entryRows, eowRow];
    const ceiling = boundCeiling(now);
    const floor = boundFloor(now);
    bounded.forEach((row, index) => {
        const previous = bounded[index - 1];
        const next = bounded[index + 1];
        row.minStart = minStartOf(row, previous, floor);
        row.maxEnd = maxEndOf(row, next, ceiling);
    });

    // Weave in the gaps: any positive span between two consecutive rows is unaccounted time.
    return bounded.flatMap((row, index) => {
        const previous = bounded[index - 1];
        const previousEnd = previous ? effectiveEnd(previous) : '';
        if (previous && isAfter(previousEnd, row.start)) {
            const gap: TimesheetGapRow = {
                kind: 'gap',
                rowId: `gap-${previousEnd}`,
                start: previousEnd,
                end: row.start,
                minStart: previousEnd,
                maxEnd: row.start,
                isBeforeEndOfWork: row.kind === 'eow',
                ...(previous.kind === 'work' && TRAVEL_BACK_WORK_CODES.includes(previous.workCode)
                    ? {
                          precedingTravelBack: {
                              entryId: previous.entryId,
                              workCode: previous.workCode,
                              notificationId: previous.notificationId,
                              isTravelBillable: previous.isTravelBillable,
                          },
                      }
                    : {}),
            };
            return [gap, row];
        }
        return [row];
    });
}

/**
 * The travel type a follow-up leg takes: the opposite of the leg it continues, so a billable
 * journey is continued by a standby one and the other way round. This is the rule the legacy
 * `addManualEntry` applied when it was handed a preceding travel-back entry.
 *
 * @param precedingWorkCode the work code of the travel-back being continued
 * @returns the travel type for the follow-up entry
 */
export function followUpTravelType(precedingWorkCode: string): TIMERECORDING_TYPE_OF_WORK_CODES {
    return precedingWorkCode === TIMERECORDING_TYPE_OF_WORK_CODES.TRAVEL_BACK_STANDBY
        ? TIMERECORDING_TYPE_OF_WORK_CODES.TRBK
        : TIMERECORDING_TYPE_OF_WORK_CODES.TRAVEL_BACK_STANDBY;
}
